import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Platform, PlatformModules } from '@app/classes';
import { FeatureFlag } from '@app/enums';
import { AuthService, FeatureAcknowledgementService, NotifyService } from '@app/services';
import { Features } from '@app/services/feature-acknowledgement.service';
import { FeatureService } from '@app/services/feature.service';
import { TranslateService } from '@ngx-translate/core';
import { without } from 'lodash-es';
import { upsellCardMetaData } from '../../meta/upsell-meta';
import { upsellV2CardMetaData } from '../../meta/upsell-v2-meta';

@Component({
    templateUrl: './add-ons.template.html',
    styleUrls: ['./add-ons.style.scss'],
})
export class AddOnsComponent implements OnInit {
    activeModules: PlatformModules[] = [];
    inactiveModules: PlatformModules[] = [];
    activeModulesV2: PlatformModules[] = [];
    inactiveModulesV2: PlatformModules[] = [];
    allModulesV2: PlatformModules[];
    allModules: PlatformModules[];
    upsellCardMetaData = upsellCardMetaData;
    upsellCardMetaDataV2 = upsellV2CardMetaData;
    modules = Platform.modules;
    orderOfModules: PlatformModules[] = [
        this.modules.timeOff,
        this.modules.recruiting,
        this.modules.payroll,
        this.modules.performance,
    ];
    modulesToExclude: PlatformModules[] = [
        this.modules.survey,
        this.modules.businessInsurance,
        this.modules.timeTracking,
        this.modules.benefitsPlus,
    ];
    modulesToExcludeV2 = [this.modules.survey, 'Scheduling'];
    orderOfModulesV2: PlatformModules[] = [
        this.modules.benefits,
        this.modules.timeOff,
        this.modules.payroll,
        this.modules.recruiting,
        this.modules.timeTracking,
        this.modules.benefitsPlus,
        this.modules.performance,
        this.modules.businessInsurance,
    ];
    upsellV2Enabled = false;
    hasBenefitsPlus = false;
    readonly payrollModuleName = 'Payroll';

    constructor(
        private readonly featureAcknowledgementService: FeatureAcknowledgementService,
        private readonly auth: AuthService,
        private readonly notify: NotifyService,
        protected readonly featureService: FeatureService,
        private readonly translateService: TranslateService,
        private readonly router: Router
    ) {
        // This page is not available in French
        if (this.translateService.currentLang === 'fr_CA') {
            this.router.navigate(['/']);
        }
        this.allModules = Object.values(Platform.modules).filter(this.filterSurvey);
        this.activeModules = this.auth.company
            ? this.auth.company.modules.map((m) => m.name as PlatformModules).filter(this.filterSurvey)
            : [];

        this.inactiveModules = this.allModules.filter((moduleName: PlatformModules) => {
            return !this.activeModules.includes(moduleName);
        });

        this.allModulesV2 = Object.values(Platform.modules).filter(this.filterSurveyV2);

        this.activeModulesV2 = this.auth.company
            ? this.auth.company.modules.map((m) => m.name as PlatformModules).filter(this.filterSurveyV2)
            : [];

        this.inactiveModulesV2 = without(
            this.allModulesV2,
            ...this.activeModulesV2.filter((module) => module !== this.modules.benefits)
        );

        if (this.inactiveModulesV2.includes(this.payrollModuleName) && this.auth.company.isSelfServeWithPayroll) {
            // Self Serve w/ Payroll can be in a state where the module is not yet activated
            // Swap it to active so that it displays correctly in Add-ons
            this.inactiveModules = this.inactiveModules.filter((e) => e !== this.payrollModuleName);
            this.inactiveModulesV2 = this.inactiveModulesV2.filter((e) => e !== this.payrollModuleName);
            this.activeModules.push(this.payrollModuleName);
            this.activeModulesV2.push(this.payrollModuleName);
        }

        // Added sort to for proper ordering of inactive modules
        this.inactiveModules.sort((a, b) => {
            return this.orderOfModules.indexOf(a) - this.orderOfModules.indexOf(b);
        });
    }

    async ngOnInit(): Promise<void> {
        try {
            this.featureAcknowledgementService.acknowledgeFeature(Features.inAppUpsell);

            this.upsellV2Enabled = await this.featureService.has(FeatureFlag.upsellV2);

            this.getInactiveModulesV2();
        } catch (err) {
            this.notify.error(err);
        }
    }

    filterSurvey = (moduleName: PlatformModules): boolean => {
        return !this.modulesToExclude.includes(moduleName);
    };

    filterSurveyV2 = (moduleName: PlatformModules): boolean => {
        return !this.modulesToExcludeV2.includes(moduleName);
    };

    getInactiveModulesV2(): void {
        this.inactiveModulesV2 = this.isBenefitsPlusActive();
        this.inactiveModulesV2.sort((a, b) => {
            return this.orderOfModulesV2.indexOf(a) - this.orderOfModulesV2.indexOf(b);
        });
    }

    isBenefitsPlusActive(): PlatformModules[] {
        if (this.activeModulesV2.includes('Benefits Plus')) {
            this.hasBenefitsPlus = true;
            return this.inactiveModulesV2.filter((module) => module !== this.modules.benefits);
        }

        return this.inactiveModulesV2.filter((module) => module !== this.modules.benefitsPlus);
    }
}
